import { makeAutoObservable } from "mobx";
import { buildDisclosureYearKey } from "../domains/targetSetup";

const normalizeTargetScopeId = (value) => {
  const raw = value?._id ?? value?.id ?? value;
  if (raw === null || raw === undefined || raw === "") return null;
  const normalized = String(raw).trim();
  return normalized && normalized !== "[object Object]" ? normalized : null;
};

const resolveTargetOrgKey = (target, fallbackOrgId) => {
  const orgId = normalizeTargetScopeId(target?.organizationId);
  if (orgId) return orgId;

  const companyId = normalizeTargetScopeId(target?.companyId);
  if (companyId) return companyId;

  return normalizeTargetScopeId(fallbackOrgId);
};

class TargetSetupStore {
  selectedFrameworks = [];
  frameworks = [];
  activeCategory = "Environmental";
  disclosures = [];
  searchDisclosure = "";
  reportingBoundary = null;

  orgList = [];
  orgTree = [];
  filteredOrgTree = [];
  orgTreeLoaded = false;
  reportingYear = "All";

  /** "yearly" | "monthly" — loaded from API alongside disclosure targets */
  targetFrequency = "yearly";
  targetFrequencyLoaded = false;

  /** @type {Map<string, Map<string, object>>} disclosureId_year -> Map<orgId, record> */
  targetsByDisclosure = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  get filteredDisclosures() {
    let result = this.disclosures.filter(
      (d) => d.category === this.activeCategory,
    );
    if (this.selectedFrameworks.length > 0) {
      const selected = new Set(this.selectedFrameworks);
      result = result.filter((d) => selected.has(d.framework));
    }
    if (!this.searchDisclosure) return result;
    const search = this.searchDisclosure.toLowerCase();
    return result.filter(
      (d) =>
        d.code?.toLowerCase().includes(search) ||
        d.title?.toLowerCase().includes(search),
    );
  }

  setFrameworks = (frameworks) => {
    this.frameworks = Array.isArray(frameworks) ? frameworks : [];

    const availableFrameworkCodes = new Set(
      this.frameworks.map((framework) => framework.code).filter(Boolean),
    );

    this.selectedFrameworks = this.selectedFrameworks.filter((framework) =>
      availableFrameworkCodes.has(framework),
    );
  };

  setSelectedFrameworks = (v) => {
    this.selectedFrameworks = v;
  };

  setActiveCategory = (category) => {
    this.activeCategory = category;
  };

  setDisclosures = (disclosures) => {
    this.disclosures = disclosures;
  };

  setReportingYear = (v) => {
    this.reportingYear = v;
  };

  hydrateDisclosureData = ({
    disclosures = [],
    reportingBoundary = null,
    frameworks,
  }) => {
    this.disclosures = disclosures;
    this.reportingBoundary = reportingBoundary;
    if (frameworks !== undefined) {
      this.setFrameworks(frameworks);
    }
  };

  setSearchDisclosure = (search) => {
    this.searchDisclosure = search;
  };

  setReportingBoundary = (boundary) => {
    this.reportingBoundary = boundary;
  };

  setOrgList = (list) => {
    this.orgList = list;
  };

  setOrgTree = (tree) => {
    this.orgTree = tree;
  };

  setFilteredOrgTree = (tree) => {
    this.filteredOrgTree = tree;
  };

  setOrgScope = ({ tree = [], list = [], filteredTree = [] }) => {
    this.orgTree = tree;
    this.orgList = list;
    this.filteredOrgTree = filteredTree;
    this.orgTreeLoaded = true;
  };

  setTargetFrequency = (freq) => {
    this.targetFrequency = freq === "monthly" ? "monthly" : "yearly";
    this.targetFrequencyLoaded = true;
  };

  setTargetFrequencyLoaded = (loaded) => {
    this.targetFrequencyLoaded = Boolean(loaded);
  };

  setDisclosureTargets = (disclosureId, year, targets) => {
    const disclosureKey = buildDisclosureYearKey(disclosureId, year);
    const orgMap = new Map();
    for (const t of targets) {
      // null organizationId = parent company level; use companyId as key
      const orgId = resolveTargetOrgKey(t);
      if (!orgId) continue;
      orgMap.set(orgId, t);
    }
    this.targetsByDisclosure.set(disclosureKey, orgMap);
  };

  setOrgTarget = (disclosureId, year, orgId, record) => {
    const disclosureKey = buildDisclosureYearKey(disclosureId, year);
    const normalizedOrgId = resolveTargetOrgKey(record, orgId);
    if (!normalizedOrgId) return;

    if (!this.targetsByDisclosure.has(disclosureKey)) {
      this.targetsByDisclosure.set(disclosureKey, new Map());
    }
    this.targetsByDisclosure.get(disclosureKey).set(normalizedOrgId, record);
  };

  removeOrgTarget = (disclosureId, year, orgId) => {
    const disclosureKey = buildDisclosureYearKey(disclosureId, year);
    const normalizedOrgId = String(orgId ?? "").trim();
    if (!normalizedOrgId || !this.targetsByDisclosure.has(disclosureKey))
      return;

    const orgMap = this.targetsByDisclosure.get(disclosureKey);
    orgMap.delete(normalizedOrgId);

    if (orgMap.size === 0) {
      this.targetsByDisclosure.delete(disclosureKey);
    }
  };

  getOrgTargets = (disclosureId, year) => {
    const disclosureKey = buildDisclosureYearKey(disclosureId, year);
    return this.targetsByDisclosure.get(disclosureKey) || new Map();
  };

  reset = () => {
    // this.selectedFrameworks = [];
    // this.frameworks = [];
    this.activeCategory = "Environmental";
    // this.disclosures = [];
    this.searchDisclosure = "";
    this.reportingBoundary = null;
    this.orgList = [];
    this.orgTree = [];
    this.filteredOrgTree = [];
    this.orgTreeLoaded = false;
    this.targetFrequency = "yearly";
    this.targetFrequencyLoaded = false;
    this.targetsByDisclosure = new Map();
  };
}

export const targetSetupStore = new TargetSetupStore();
